import { ROOT_ID, type NodeRecord, type Scene } from '@flobi/artwork-core';

const DRAWABLE = new Set(['rect', 'ellipse', 'polygon', 'star', 'line', 'path', 'text', 'image', 'group', 'boolean', 'blend', 'cloner']);
const VECTOR = new Set(['rect', 'ellipse', 'polygon', 'star']);

/** Read the effective selection in stacking order, without silently dropping locked layers. */
export function geometrySelection(scene: Scene, ids: readonly string[]): string[] {
  const chosen = scene.topmost([...ids]);
  if (chosen.length < 2 || chosen.some(id => {
    const node = scene.get(id);
    return !node || scene.lockedHere(id) || node.isMask || node.visible === false || !DRAWABLE.has(node.type);
  })) return [];
  const selected = new Set(chosen);
  return scene.paintOrder().filter(id => selected.has(id));
}

/** The current boolean renderer understands vector outlines and plain groups of them. */
export function canCombineSelection(scene: Scene, ids: readonly string[]): boolean {
  const vector = (id: string): boolean => {
    const node = scene.get(id);
    if (!node || node.isMask) return false;
    if (VECTOR.has(node.type)) return true;
    // Boolean arithmetic uses filled areas, not the expanded width of a stroke.
    // Open line art otherwise disappears when Subtract is applied to its centreline.
    if (node.type === 'path') return node.path.subpaths.length > 0 && node.path.subpaths.every(subpath => subpath.closed);
    const children = scene.childrenOf(id).filter(child => scene.get(child)?.visible !== false);
    return node.type === 'group' && children.length > 0 && children.every(vector);
  };
  const chosen = geometrySelection(scene, ids);
  return chosen.length > 1 && chosen.every(vector);
}

/** Isolate the selection so a mask never clips unrelated siblings. One atomic undo step. */
export function planSelectionMask(scene: Scene, ids: readonly string[]): { maskId: string; ops: unknown[] } | null {
  const chosen = geometrySelection(scene, ids);
  if (chosen.length < 2) return null;
  const maskId = chosen[0]!;
  const front = chosen[chosen.length - 1]!;
  return {
    maskId,
    ops: [
      { op: 'create', ref: 'maskGroup', parentId: scene.parentOf(front) ?? ROOT_ID, position: { before: front }, node: { type: 'group', name: 'Mask group' } },
      ...chosen.map(id => ({ op: 'move', id, parentId: '$maskGroup', position: 'front', keepWorldTransform: true })),
      { op: 'set', id: maskId, props: { isMask: true } },
    ],
  };
}

export function selectionMask(scene: Scene, id: string): NodeRecord | null {
  const node = scene.get(id);
  if (node?.isMask) return node;
  if (node?.type !== 'group') return null;
  return scene.childrenOf(id).map(child => scene.get(child)).find(child => child?.isMask) ?? null;
}
